import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { DatasetActionContainerService, DatasetEvents } from 'app/main/components/dataset/services/dataset-action-container.service';
import { DatasetNavigatorService } from 'app/main/components/dataset/services/dataset-navigator.service';
import { DatasetService } from 'app/main/components/dataset/services/dataset.service';
import { BaseStepViewComponent } from 'app/main/components/dataset/components/common-step/base-step-view.class';
import { takeUntil } from 'rxjs/operators';
import { DatasetRecordService } from 'app/main/components/dataset/services/dataset-record.service';
import { AppService } from 'app/services/app.service';
import { IStepConfig } from 'app/interfaces';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { ConfirmDialogComponent } from 'app/main/dialogs/confirm-dialog/dialog.component';
import { GOOGLE_API_KEY, GoogleMapsLoaderService } from 'app/services/google-maps-loader.service';
import { FormControl } from '@angular/forms';

@Component({
    selector: 'common-map-step',
    templateUrl: './step.component.html',
    styleUrls: ['./step.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommonMapStepComponent extends BaseStepViewComponent implements OnInit{
	
	@Input() public stepConfig: IStepConfig;
	@Input() public stepIndex: number;
	public formErrors: any;
	public markers: IMapMarker[] = [];
	// google maps zoom level
	public zoom = 8;
	// initial center position for the map
	public lat = 45.468702;
	public lng = 9.190789;
	public mapReady = new BehaviorSubject<boolean>(false);
	public searchTextFormControl = new FormControl('');
	public selectedPositionInfo: google.maps.GeocoderResult;
	public geosearching = false;
	public infoContent = '';
	public apiLoaded = false;
	@ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;

	// IMPORTANTE: questi binding devono avere RIFERIMENTO STABILE tra i cicli di change
	// detection. @angular/google-maps richiama setCenter/setOptions ad ogni cambio di
	// reference: un getter che ritorna un nuovo oggetto ogni volta manda la mappa in loop
	// di CD e blocca la pagina. Quindi sono proprietà aggiornate solo quando i valori cambiano.
	public mapCenter: google.maps.LatLngLiteral = { lat: this.lat, lng: this.lng };
	public mapOptions: google.maps.MapOptions = { disableDefaultUI: false, zoomControl: false };

	private updateCenter(): void {
		if (this.mapCenter.lat !== this.lat || this.mapCenter.lng !== this.lng) {
			this.mapCenter = { lat: this.lat, lng: this.lng };
		}
	}

	private buildMarkerOptions(m: IMapMarker): google.maps.MarkerOptions {
		const opts: google.maps.MarkerOptions = { draggable: !!m.draggable };
		if ((window as any).google && (window as any).google.maps) {
			opts.animation = (window as any).google.maps.Animation.DROP;
		}
		return opts;
	}

	get record(): any{
		return this.datasetRS.record.value;
	}

	get staticMapUrl(): string{
		return this.record.point_map_url;
	}

	get savedCoords(): any{
		if(!this.record) return null;
		if(!this.record.properties) return null;
		if(!this.record.properties.geo_position || !this.record.properties.geo_position.value) return null;
		if(typeof(this.record.properties.geo_position.value) === 'string') return JSON.parse(this.record.properties.geo_position.value); 
		return this.record.properties.geo_position.value;
	}

	get datasetCode(): string{
		return this.datasetACS.datasetCode;
	}
	get address(): string{
		if(['meeting_points', 'trip_destinations'].includes(this.datasetCode)){
			return this.reduceAddress(this.record && this.record.address);
		}else{
			return null;
		}
	}

	constructor(
		public appService: AppService,
		public datasetRS: DatasetRecordService,
		public datasetACS: DatasetActionContainerService,
		protected datasetNavigatorService: DatasetNavigatorService,
		public datasetService: DatasetService,
		protected matDialog: MatDialog,
		public mapsLoader: GoogleMapsLoaderService
	){
		super(datasetACS, datasetNavigatorService);
	}

	reduceAddress(obj: any): string{
		let address = '';
		if(obj){
			if(obj.street) address += obj.street;
			if(obj.number) address += ' ' + obj.number;
			if(address && (obj.city || obj.province || obj.zip_code || obj.region || obj.nation_code)) address += ', ';
			if(obj.city) address += ' ' + obj.city;
			if(obj.province) address += ' (' + obj.province + ')';
			if(obj.zip_code) address += ' ' + obj.zip_code;
			if(obj.region) address += ' ' + obj.region;
			if(obj.nation_code) address += ' ' + obj.nation_code;
		}
		return address;
	}

	ngOnInit(): void{
		super.ngOnInit();

		this.initialLocation()
		.catch(e => {
			console.error(e);
		});
	}

	search(): void{
		if(!this.searchTextFormControl.value || this.searchTextFormControl.value.length < 2) return;
		this.geosearching = true;
		this.getAddressInfo(this.searchTextFormControl.value)
		.then(result => {
			this.setPosition(result);
			this.geosearching = false;
		}, error => {
			this.geosearching = false;
		});
	}

	async initialLocation(): Promise<void>{
		await this.mapsLoader.load();
		// Lo script Google Maps è pronto: solo ora <google-map> può istanziarsi
		// (@angular/google-maps non carica l'API da sé, a differenza di @agm).
		this.apiLoaded = true;

		if(this.savedCoords && this.savedCoords.lat && this.savedCoords.lng){
			this.setCoordinate(this.savedCoords.lat, this.savedCoords.lng);
			if(this.savedCoords.zoom) this.zoom = this.savedCoords.zoom;
		}else{
			this.getAddressInfo(this.address)
			.then(result => {
				this.setPosition(result);
			}, error => {

			});
		}
	}

	setMarker(data: any): void{
		if(this.markers.length){
			this.markers[0].lat = data.lat;
			this.markers[0].lng = data.lng;
			this.markers[0].label = data.label;
			this.markers[0].position = { lat: data.lat, lng: data.lng };
		}else{
			const marker: IMapMarker = {
				lat: data.lat,
				lng: data.lng,
				draggable: true,
				label: data.label,
				position: { lat: data.lat, lng: data.lng }
			};
			marker.options = this.buildMarkerOptions(marker);
			this.markers.push(marker);
		}
		this.lat = this.markers[0].lat;
		this.lng = this.markers[0].lng;
		this.updateCenter();
		if(this.zoom < 12 )this.zoom = 16;
		this.fetchMarkerInfo(this.markers[0]);
	}

	async getAddressInfo(address): Promise<google.maps.GeocoderResult>{
		await this.mapsLoader.load();
		const geocoder: google.maps.Geocoder = new window['google'].maps.Geocoder();
		return new Promise<google.maps.GeocoderResult>((resolve, reject) => {
			geocoder.geocode({ 
				address
			}, (results: any, status: any): void => {
				if(status === 'OK'){
					if(results.length > 0 && results[0]){
						return resolve(results[0]);
					}
				}
				reject();
			});
		});
	}

	async getCoordinateInfo(lat, lng): Promise<google.maps.GeocoderResult>{
		await this.mapsLoader.load();
		const geocoder: google.maps.Geocoder = new window['google'].maps.Geocoder();
		return new Promise<google.maps.GeocoderResult>((resolve, reject) => {
			geocoder.geocode({ 
				location: {
					lat, lng
				}
			}, (results: any, status: any): void => {
				if(status === 'OK'){
					if(results.length > 0 && results[0]){
						return resolve(results[0]);
					}
				}
				reject();
			});
		});
	}

	async fetchSelectedPositionInfo(lat, lng): Promise<void>{
		this.selectedPositionInfo = await this.getCoordinateInfo(lat, lng);
	}

	async fetchMarkerInfo(marker: IMapMarker): Promise<void>{
		this.selectedPositionInfo = await this.getCoordinateInfo(marker.lat, marker.lng);
		marker.label = this.selectedPositionInfo.formatted_address;
		marker.meta = this.selectedPositionInfo;
	}

	mapClicked(event: google.maps.MapMouseEvent): void{
		this.setCoordinate(event.latLng.lat(), event.latLng.lng());
	}

	clickedMarker(markerRef: MapMarker, m: IMapMarker): void{
		this.infoContent = m.label;
		if(this.infoWindow) this.infoWindow.open(markerRef);
	}

	markerDragEnd(m: IMapMarker, $event: google.maps.MapMouseEvent): void{
		this.setMarker({
			lat: $event.latLng.lat(),
			lng: $event.latLng.lng()
		});
	}

	generateStaticMap(): string{
		if(!this.selectedPositionInfo) return null;
		let url = 'https://maps.googleapis.com/maps/api/staticmap?';
		url += '&zoom=' + this.zoom;
		url += '&size=600x300';
		url += '&maptype=roadmap';
		if(this.markers && this.markers.length > 0){
			for (const marker of this.markers) {
				url += '&markers=color:red|' + marker.lat + ',' + marker.lng;
			}
		}else{
			url += '&center=' + this.selectedPositionInfo.geometry.location.lat() + ',' + this.selectedPositionInfo.geometry.location.lng();
		}
		
		url += '&key=' + GOOGLE_API_KEY;

		return url;
	}

	onSave(): void{
		if(!this.selectedPositionInfo) return;
		let message = 'Vuoi salvare solo la mappa o anche l\'indirizzo?';
		if(this.address) message += '<br>Indirizzo attuale: "' + this.address + '" ';
		const newAddressObj = this.componentsToAddressObject(this.selectedPositionInfo.address_components);
		message += '<br>Nuovo Indirizzo: "' + this.reduceAddress(newAddressObj) + '"';

		this.matDialog.open(ConfirmDialogComponent, {
			minWidth: '550px',
			data: {
				contentMessage: message,
				positiveText: 'Salva tutto',
				negativeText: 'Annulla',
				neutralText: 'Solo la mappa'
			}
		}).afterClosed()
		.subscribe(result => {
			if(result === false) return;
			this.execSave(result === true ? 'full' : 'only');
		});
		
	}

	componentsToAddressObject(components: google.maps.GeocoderAddressComponent[]): any{
		const addressObj: any = {};

		for (const component of components) {
			if(component.types.includes('street_number')){
				addressObj.number = component.long_name;
			}else if(component.types.includes('route')){
				addressObj.street = component.long_name;
			}else if(component.types.includes('administrative_area_level_3')){
				addressObj.city = component.long_name;
			}else if(component.types.includes('locality')){
				addressObj.locality = component.long_name;
			}else if(component.types.includes('administrative_area_level_2')){
				addressObj.province = component.short_name;
			}else if(component.types.includes('administrative_area_level_1')){
				addressObj.region = component.long_name;
			}else if(component.types.includes('country')){
				addressObj.nation_code = component.short_name;
			}else if(component.types.includes('postal_code')){
				addressObj.zip_code = component.long_name;
			}
		}

		return addressObj;
	}

	execSave(saveType: string): void{
		let formData = { 
			recordId: this.datasetRS.recordId, 
			saveType,
			geoPosition: {
				lat: this.selectedPositionInfo.geometry.location.lat(),
				lng: this.selectedPositionInfo.geometry.location.lng(),
				zoom: this.zoom
			},
			staticMapURL: this.generateStaticMap()
		};
		
		if(this.stepConfig.options && typeof(this.stepConfig.options.getCallParams) === 'function'){
			formData = this.stepConfig.options.getCallParams(this.datasetACS, this.datasetRS.record.value, formData);
		}
		formData['address'] = this.componentsToAddressObject(this.selectedPositionInfo.address_components);
		this.datasetACS.loading.next(true);
		const baseRoute = '/dataset/' + this.datasetACS.datasetConfig.ajaxDatasetCode;
		const route = baseRoute + '/command/set_map_position';
		this.datasetService.post<any>(route, formData)
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe({
			next: (response) => {
				this.datasetACS.loading.next(false);
				this.datasetACS.datasetEvent.next({eventName: DatasetEvents.EDITED });
				
				this.datasetRS.reload()
				.subscribe(value => {
					this.goNextStep();
				});
			},
			error: response => {
				this.datasetACS.loading.next(false);
				if(response && response.error && response.error.errors) this.formErrors = response.error.errors;
				else this.formErrors = null;
				
			}
		});
	}

	setPosition(result: google.maps.GeocoderResult): void{
		if(this.datasetCode === 'meeting_points'){
			this.setMarker({
				lat: result.geometry.location.lat(),
				lng: result.geometry.location.lng()
			});
		}else{
			this.lat = result.geometry.location.lat();
			this.lng = result.geometry.location.lng();
			this.updateCenter();
			this.fetchSelectedPositionInfo(this.lat, this.lng);
		}
	}

	setCoordinate(lat, lng): void{
		if(this.datasetCode === 'meeting_points'){
			this.setMarker({
				lat,
				lng
			});
		}else{
			this.lat = lat;
			this.lng = lng;
			this.updateCenter();
			this.fetchSelectedPositionInfo(lat, lng);
		}
	}

	resetPosition(): void{
		this.markers = [];
		this.selectedPositionInfo = null;
		this.initialLocation();
	}
}

interface IMapMarker{
	lat: number;
	lng: number;
	label?: string;
	draggable?: boolean;
	meta?: any;
	position?: google.maps.LatLngLiteral;
	options?: google.maps.MarkerOptions;
}
